import { HStack } from '@sgme/ui';
import type { ColDef, GridApi, GridReadyEvent, RowClickedEvent, StateUpdatedEvent } from 'ag-grid-community';
import { AgGridReact } from 'ag-grid-react';
import { formatDate } from 'date-fns';
import { useCallback, useEffect, useImperativeHandle, useRef } from 'react';

import { selectWorkspaceBlotterColumnState } from '@/api/workspace/selectors.ts';
import type { BlotterGridComponent } from '@/App/Blotter/grids/BlotterGridComponent.tsx';
import { getRowId } from '@/App/Blotter/grids/getRowId.ts';
import { useColumnDefinitions } from '@/App/Blotter/grids/order/useColumnDefinitions.tsx';
import { sidebarConfiguration } from '@/App/Blotter/grids/sidebarConfiguration.ts';
import { useGridEventsTrackers } from '@/App/Blotter/useGridEventsTrackers.ts';
import { blotterSlice } from '@/store/blotter/slice.ts';
import type { BlotterOrderEntry } from '@/store/blotter/types/BlotterOrderEntry.ts';
import { useAppDispatch, useAppSelector } from '@/store/hooks.ts';

const defaultColDef = {
  minWidth: 100,
  filter: false,
  sortable: true,
  suppressHeaderMenuButton: true,
  //cellClass: 'text-end',
} as const satisfies ColDef<BlotterOrderEntry>;

export const BlotterOrderGrid: BlotterGridComponent = ({ ref }) => {
  const dispatch = useAppDispatch();

  const gridEventsTrackers = useGridEventsTrackers('order');

  const gridApiRef = useRef<{ api: GridApi<BlotterOrderEntry> | undefined }>({ api: undefined });

  const entries = useAppSelector(blotterSlice.selectors.getOrderEntries);
  const initialColummnState = useAppSelector(selectWorkspaceBlotterColumnState('orderTab')) ?? [];

  const columnDefs = useColumnDefinitions();

  const handleGridReady = useCallback(
    ({ api }: GridReadyEvent<BlotterOrderEntry>) => {
      gridApiRef.current.api = api;
      api.applyColumnState({ state: initialColummnState, applyOrder: true });
    },
    [initialColummnState],
  );

  const handleStateUpdated = useCallback(({ api }: StateUpdatedEvent<BlotterOrderEntry>) => {
    dispatch(blotterSlice.actions.saveOrderGridColumnState(api.getColumnState()));
  }, []);

  const handleRowClicked = useCallback(({ api }: RowClickedEvent<BlotterOrderEntry>) => {
    if (api.getFocusedCell()?.column.getColId() === 'actions') {
      return;
    }
    // TODO : view order in grid
  }, []);

  useImperativeHandle(ref, () => ({
    isExportEnabled: () => entries.length > 0,
    export: () => {
      const api = gridApiRef.current.api;
      if (!api) {
        return;
      }

      const columnKeys = api
        .getColumnState()
        .filter(({ colId, hide }) => !hide && colId !== 'actions')
        .map(({ colId }) => colId);

      api.exportDataAsExcel({
        columnKeys,
        fileName: `Export-SGMarketsFX-orders-${formatDate(new Date(), 'yyyyMMdd-HHmmss')}.xlsx`,
      });
    },
  }));

  useEffect(() => {
    gridApiRef.current.api?.applyColumnState({ state: initialColummnState });
  }, [initialColummnState]);

  return (
    <HStack className="h-100">
      <AgGridReact<BlotterOrderEntry>
        className="ag-theme-sg-bootstrap-condensed flex-grow-1"
        theme="legacy"
        columnDefs={columnDefs}
        rowData={entries}
        getRowId={getRowId}
        onGridReady={handleGridReady}
        defaultColDef={defaultColDef}
        onRowClicked={handleRowClicked}
        onStateUpdated={handleStateUpdated}
        onSortChanged={gridEventsTrackers.handleSortChanged}
        onFilterChanged={gridEventsTrackers.handleFilterChanged}
        onToolPanelVisibleChanged={gridEventsTrackers.handleToolPanelVisibleChange}
        onColumnVisible={gridEventsTrackers.handleColumnVisible}
        onColumnMoved={gridEventsTrackers.handleColumnMoved}
        onColumnResized={gridEventsTrackers.handleColumnResized}
        suppressCellFocus
        sideBar={sidebarConfiguration}
      />
    </HStack>
  );
};
